import { useRef } from "react";
import { motion, useScroll, useInView } from "framer-motion";
import { Eyebrow, Reveal } from "./bits";

const stages = [
  {
    slug: "goal",
    n: "01",
    title: "Your Goal",
    desc: "It begins with a conversation about what you actually want from life — not from the market.",
    chip: "45-min discovery call",
  },
  {
    slug: "strategy",
    n: "02",
    title: "Strategy",
    desc: "We translate your goals into a clear, personalised roadmap with defined time horizons.",
    chip: "Personalised roadmap",
  },
  {
    slug: "investment",
    n: "03",
    title: "Investment",
    desc: "Curated mutual fund allocations matched to your risk profile. No noise, no clutter.",
    chip: "Curated portfolio",
  },
  {
    slug: "review",
    n: "04",
    title: "Review",
    desc: "Periodic check-ins keep your plan aligned as markets move and life evolves.",
    chip: "Periodic reviews",
  },
  {
    slug: "growth",
    n: "05",
    title: "Growth",
    desc: "Discipline and time do the heavy lifting. Compounding rewards the patient.",
    chip: "Compounding over time",
  },
];

const Stage = ({ stage }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { margin: "-42% 0px -42% 0px" });
  return (
    <div ref={ref} data-testid={`journey-stage-${stage.slug}`} className="relative pb-20 pl-20 last:pb-0 md:pl-28">
      <span
        className={`absolute left-[7px] top-2 h-3.5 w-3.5 rounded-full border transition-all duration-700 md:left-[39px] ${
          inView
            ? "border-emerald-500 bg-emerald-400 shadow-[0_0_24px_rgba(16,185,129,0.7)]"
            : "border-[#10202E]/25 bg-white"
        }`}
      />
      <div
        className={`transition-all duration-700 ${
          inView ? "translate-y-0 opacity-100" : "translate-y-3 opacity-35"
        }`}
      >
        <span className="font-mono text-[11px] tracking-[0.3em] text-emerald-600/80">{stage.n}</span>
        <h3 className="mt-2 font-display text-3xl text-[#10202E] sm:text-4xl">{stage.title}</h3>
        <p className="mt-3 max-w-md text-base font-light leading-relaxed text-[#4E6A82]">{stage.desc}</p>
        <span className="mt-4 inline-block rounded-full border border-[#10202E]/10 bg-white/60 px-3.5 py-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-[#6E8698]">
          {stage.chip}
        </span>
      </div>
    </div>
  );
};

export const Journey = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start 0.72", "end 0.7"] });

  return (
    <section
      id="journey"
      data-testid="journey-section"
      className="relative border-y border-[#10202E]/[0.06] bg-[#E8EFF4] py-28 md:py-40"
    >
      <div className="mx-auto max-w-6xl px-6">
        <Reveal>
          <Eyebrow>04 — The Journey</Eyebrow>
        </Reveal>
        <Reveal delay={0.08}>
          <h2 className="mt-6 max-w-3xl font-display text-4xl leading-[1.08] tracking-tight sm:text-5xl lg:text-6xl">
            From intention to <span className="italic text-emerald-600">action.</span>
          </h2>
        </Reveal>

        <div ref={ref} className="relative mt-20 max-w-2xl">
          <div className="absolute bottom-8 left-[13px] top-2 w-px bg-[#10202E]/10 md:left-[45px]" />
          <motion.div
            data-testid="journey-progress-line"
            style={{ scaleY: scrollYProgress }}
            className="absolute bottom-8 left-[13px] top-2 w-px origin-top bg-gradient-to-b from-emerald-500 via-emerald-600 to-amber-500 md:left-[45px]"
          />
          {stages.map((s) => (
            <Stage key={s.slug} stage={s} />
          ))}
        </div>
      </div>
    </section>
  );
};
